/**
 * Pricing.jsx — HealHub Pricing Page
 *
 * Shows Free vs Plus plan comparison.
 * Upgrade button is wired to navigate('/dashboard') for now.
 *
 * ─── RAZORPAY INTEGRATION ────────────────────────────────────────────────
 * When adding Razorpay:
 * 1. Add to public/index.html:
 *    <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
 * 2. Add to .env:
 *    REACT_APP_RAZORPAY_KEY_ID=rzp_live_xxx
 * 3. Replace the handleUpgrade function body with the Razorpay checkout code
 *    shown in the comment below.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect }  from 'react';
import { Link, useNavigate }    from 'react-router-dom';
import { auth }                 from '../firebase';
import { signOut }              from 'firebase/auth';
import { MdOutlineLightMode, MdModeNight, MdFilterVintage } from 'react-icons/md';
import { FaUserCircle, FaCheck, FaTimes } from 'react-icons/fa';
import { authFetch, getUserClaims }       from '../utils/authFetch';
import '../styles/global.css';

// ─── Plan feature table ───────────────────────────────────────────────────
const FEATURES = [
  { label: 'Quick Note AI insights',       free: '30/month',    paid: 'Unlimited'  },
  { label: 'Diary AI insights',            free: '1/day',       paid: 'Unlimited'  },
  { label: 'Chat with Saksham',            free: '5 msgs/day',  paid: 'Unlimited'  },
  { label: 'Chat word budget',             free: '~600 words/day', paid: 'Unlimited' },
  { label: 'Wellness Report',              free: '1 lifetime',  paid: 'Monthly + Weekly' },
  { label: 'Diary entries visible',        free: 'Last 10',     paid: 'All entries' },
  { label: 'Saksham memory (cross-session)', free: false,       paid: true },
  { label: 'Weekly mood report',           free: false,         paid: true },
  { label: 'All assessment tests (12+)',   free: true,          paid: true },
  { label: 'Chattr (anonymous chat)',      free: true,          paid: true },
  { label: 'Community Feed',              free: true,          paid: true },
  { label: 'Focus Room',                  free: true,          paid: true },
  { label: 'Wellness Games & Stories',    free: true,          paid: true },
  { label: 'Wellness Roadmap',            free: true,          paid: true },
  { label: 'Crisis support (always free)', free: true,         paid: true },
];

const Tick  = () => <FaCheck style={{ color: '#2e7d32', fontSize: '0.9rem' }} />;
const Cross = () => <FaTimes style={{ color: '#bdbdbd', fontSize: '0.9rem' }} />;

const renderCell = (val) => {
  if (val === true)  return <Tick />;
  if (val === false) return <Cross />;
  return <span style={{ fontSize: '0.88rem', color: 'var(--text-color)', fontWeight: 500 }}>{val}</span>;
};

export default function Pricing() {
  const navigate = useNavigate();

  const [theme,          setTheme]          = useState(localStorage.getItem('theme') || 'light-theme');
  const [showInsightMenu, setShowInsightMenu] = useState(false);
  const [isPaid,         setIsPaid]         = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [subscription,   setSubscription]   = useState(null); // { paidUntilReadable, daysLeft }

  const hideTimeout = { current: null };

  useEffect(() => {
    document.body.className = theme;
    const load = async () => {
      const claims = await getUserClaims();
      setIsPaid(claims.paid);
      if (claims.paid) {
        const res  = await authFetch('/api/usage');
        const data = await res.json();
        setSubscription(data.subscription);
      }
    };
    if (auth.currentUser) load();
  }, []);

  const toggleTheme = () => {
    const themes = ['light-theme', 'dark-theme', 'zen-theme'];
    const next   = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(next); localStorage.setItem('theme', next); document.body.className = next;
  };

  const handleLogout = async () => { await signOut(auth); navigate('/'); };
  const handleInsightEnter = () => { clearTimeout(hideTimeout.current); setShowInsightMenu(true); };
  const handleInsightLeave = () => { hideTimeout.current = setTimeout(() => setShowInsightMenu(false), 150); };

  /**
   * RAZORPAY: Replace this entire function body with:
   *
   * const { orderId, amount, currency } = await authFetch('/api/payment/create-order', {
   *   method: 'POST', body: JSON.stringify({ plan: 'plus_monthly' }),
   * }).then(r => r.json());
   *
   * const rzp = new window.Razorpay({
   *   key        : process.env.REACT_APP_RAZORPAY_KEY_ID,
   *   amount, currency, order_id: orderId,
   *   name       : 'HealHub',
   *   description: 'HealHub Plus — ₹35/month unlimited access',
   *   image      : '/logo192.png',
   *   prefill    : { name: auth.currentUser?.displayName || '', email: auth.currentUser?.email || '' },
   *   theme      : { color: '#c95b3d' },
   *   handler: async (response) => {
   *     const result = await authFetch('/api/payment/verify', {
   *       method: 'POST', body: JSON.stringify(response),
   *     }).then(r => r.json());
   *     if (result.success) {
   *       await auth.currentUser.getIdToken(true); // force token refresh
   *       alert('Welcome to HealHub Plus! 💚');
   *       window.location.href = '/dashboard';
   *     }
   *   },
   * });
   * rzp.open();
   */
  const handleUpgrade = async () => {
    if (!auth.currentUser) { navigate('/login'); return; }
    setLoading(true);

    try {
      // 1. Create order via your NEW subscription route
      const orderRes = await authFetch('/api/subscription/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const orderData = await orderRes.json();

if (!orderRes.ok) {
        if (orderData.comingSoon) {
          alert('💙 Plus subscriptions are coming very soon! We\'ll notify you when ready.');
        } else {
          alert(orderData.error || 'Failed to create order.');
        }
        setLoading(false);
        return;
      }
      // 2. Open Razorpay checkout
      const rzp = new window.Razorpay({
        key        : process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount     : orderData.amount,
        currency   : orderData.currency,
        order_id   : orderData.orderId,
        name       : 'HealHub',
        description: 'HealHub Plus — ₹35/month unlimited access',
        image      : '/logo192.png',
        prefill    : {
          name : auth.currentUser?.displayName || '',
          email: auth.currentUser?.email || '',
        },
        theme: { color: '#c95b3d' },

        // 3. On successful payment — verify on backend
        handler: async (response) => {
          try {
            const verifyRes = await authFetch('/api/subscription/verify', {
              method : 'POST',
              headers: { 'Content-Type': 'application/json' },
              body   : JSON.stringify({
                razorpay_order_id  : response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature : response.razorpay_signature,
              }),
            });
            const result = await verifyRes.json();

            if (result.success) {
              // Force refresh Firebase token so paid claim is picked up
              await auth.currentUser.getIdToken(true);
              alert('Welcome to HealHub Plus! 💚');
              window.location.href = '/dashboard';
            } else {
              alert(result.error || 'Payment verification failed. Contact support.');
            }
          } catch {
            alert('Payment was received but verification failed. Please contact support.');
          }
          setLoading(false);
        },

        // 4. User closed the checkout popup
        modal: {
          ondismiss: () => { setLoading(false); },
        },
      });

      rzp.on('payment.failed', (resp) => {
        alert(`Payment failed: ${resp.error.description || 'Please try again.'}`);
        setLoading(false);
      });

      rzp.open();

    } catch (err) {
      console.error('Upgrade error:', err);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">

      {/* ─── NAVBAR ─────────────────────────────────────────────────────── */}
      <nav className="navbar">
        <div className="navbar-left">
          <Link to="/dashboard">Home</Link>
          <Link to="/resources">Resources</Link>
          <Link to="/community">Community</Link>
          <Link to="/feed" className="nav-item">Feed</Link>
          <Link to="/chatbot">Chat with Saksham</Link>
          <div className="nav-dropdown" onMouseEnter={handleInsightEnter} onMouseLeave={handleInsightLeave}>
            <span className="nav-item">Insight ▾</span>
            {showInsightMenu && (
              <div className="dropdown-menu">
                <Link to="/insight" className="dropdown-link">See Saved Diary</Link>
                <Link to="/insight" className="dropdown-link">Look at Report</Link>
              </div>
            )}
          </div>
          <Link to="/chattr" className="nav-item">Chattr</Link>
        </div>
        <div className="navbar-center">
          <span className="healhub-brand">💖🌿 HealHub</span>
          <Link to="/about" className="nav-item">About Us</Link>
        </div>
        <div className="navbar-right">
          <button onClick={toggleTheme} className="theme-switcher-btn">
            {theme === 'light-theme' ? <MdOutlineLightMode /> : theme === 'dark-theme' ? <MdModeNight /> : <MdFilterVintage />}
          </button>
          <Link to="/profile" className="profile-icon-link"><FaUserCircle size={24} /></Link>
          <button onClick={handleLogout} className="logout-btn">Log Out</button>
        </div>
      </nav>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, margin: '0 0 10px' }}>
            Simple, honest pricing 💙
          </h1>
          <p style={{ color: 'var(--text-secondary-color)', fontSize: '1rem', maxWidth: '500px', margin: '0 auto' }}>
            HealHub is free to start. Upgrade when you're ready to go deeper.
          </p>
        </div>

        {/* Current subscription status for paid users */}
        {isPaid && subscription && (
          <div style={{
            padding: '14px 20px', borderRadius: '12px',
            background: subscription.expiringSoon ? '#fff3e0' : '#e8f5e9',
            border: `1px solid ${subscription.expiringSoon ? '#ffb74d' : '#a5d6a7'}`,
            marginBottom: '24px', textAlign: 'center',
            fontSize: '0.9rem',
            color: subscription.expiringSoon ? '#e65100' : '#2e7d32',
          }}>
            {subscription.expiringSoon
              ? `⚠️ Your Plus plan expires in ${subscription.daysLeft} day${subscription.daysLeft !== 1 ? 's' : ''} (${subscription.paidUntilReadable}). Renew to keep access.`
              : `✅ HealHub Plus active until ${subscription.paidUntilReadable} (${subscription.daysLeft} days left)`
            }
          </div>
        )}

        {/* Plan Cards */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px', marginBottom: '2.5rem',
        }}>

          {/* FREE PLAN */}
          <div style={{
            borderRadius: '20px', border: '2px solid var(--border-color)',
            padding: '28px 24px', background: 'var(--card-bg, #fff)',
          }}>
            <div style={{ marginBottom: '20px' }}>
              <span style={{
                fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em',
                color: '#78909c', textTransform: 'uppercase',
              }}>
                Free Forever
              </span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '8px' }}>
                <span style={{ fontSize: '2.4rem', fontWeight: 800 }}>₹0</span>
                <span style={{ color: 'var(--text-secondary-color)', fontSize: '0.9rem' }}>/month</span>
              </div>
              <p style={{ color: 'var(--text-secondary-color)', fontSize: '0.88rem', marginTop: '8px' }}>
                Everything you need to get started on your wellness journey.
              </p>
            </div>
            <div style={{
              padding: '10px 16px', borderRadius: '10px',
              background: 'var(--border-color)', textAlign: 'center',
              fontSize: '0.88rem', color: 'var(--text-secondary-color)',
              fontWeight: 600,
            }}>
              {isPaid ? 'Your current plan after expiry' : '✅ Your current plan'}
            </div>
          </div>

          {/* PLUS PLAN */}
          <div style={{
            borderRadius: '20px',
            border: '2px solid #c95b3d',
            padding: '28px 24px',
            background: 'linear-gradient(135deg, #fff8f6, #fff)',
            position: 'relative',
            boxShadow: '0 8px 32px rgba(201,91,61,0.12)',
          }}>
            {/* Popular badge */}
            <div style={{
              position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)',
              background: '#c95b3d', color: '#fff',
              padding: '4px 18px', borderRadius: '20px',
              fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.06em',
              whiteSpace: 'nowrap',
            }}>
              MOST POPULAR
            </div>

            <div style={{ marginBottom: '20px' }}>
              <span style={{
                fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em',
                color: '#c95b3d', textTransform: 'uppercase',
              }}>
                HealHub Plus
              </span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '8px' }}>
                <span style={{ fontSize: '2.4rem', fontWeight: 800, color: '#c95b3d' }}>₹35</span>
                <span style={{ color: 'var(--text-secondary-color)', fontSize: '0.9rem' }}>/month</span>
              </div>
              <p style={{ color: 'var(--text-secondary-color)', fontSize: '0.88rem', marginTop: '8px' }}>
                28 days of unlimited access. Less than a cup of chai. ☕
              </p>
            </div>

            {isPaid ? (
              <div style={{
                padding: '10px 16px', borderRadius: '10px',
                background: '#e8f5e9', textAlign: 'center',
                fontSize: '0.88rem', color: '#2e7d32', fontWeight: 700,
              }}>
                ✅ Active — expires {subscription?.paidUntilReadable || 'soon'}
              </div>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={loading}
                style={{
                  width: '100%', padding: '13px',
                  background: loading ? '#e0e0e0' : '#c95b3d',
                  color: '#fff', border: 'none', borderRadius: '12px',
                  fontSize: '1rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                {loading ? 'Processing...' : 'Upgrade to Plus — ₹35'}
              </button>
            )}

            <p style={{
              textAlign: 'center', fontSize: '0.75rem',
              color: 'var(--text-muted)', marginTop: '10px',
            }}>
              28-day access · Auto-expires · No hidden charges
            </p>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div style={{
          borderRadius: '16px', border: '1px solid var(--border-color)',
          overflow: 'hidden', background: 'var(--card-bg, #fff)',
        }}>
          {/* Table header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 140px 140px',
            background: 'var(--card-bg-secondary, #f8f9fa)',
            padding: '14px 20px', borderBottom: '1px solid var(--border-color)',
          }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary-color)' }}>FEATURE</span>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary-color)', textAlign: 'center' }}>FREE</span>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#c95b3d', textAlign: 'center' }}>PLUS ₹35</span>
          </div>

          {FEATURES.map((f, i) => (
            <div
              key={i}
              style={{
                display: 'grid', gridTemplateColumns: '1fr 140px 140px',
                padding: '13px 20px',
                borderBottom: i < FEATURES.length - 1 ? '1px solid var(--border-color)' : 'none',
                background: i % 2 === 0 ? 'transparent' : 'var(--card-bg-secondary, #fafafa)',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '0.9rem', color: 'var(--text-color)' }}>{f.label}</span>
              <div style={{ textAlign: 'center' }}>{renderCell(f.free)}</div>
              <div style={{ textAlign: 'center' }}>{renderCell(f.paid)}</div>
            </div>
          ))}
        </div>

        {/* FAQ-style note */}
        <div style={{
          marginTop: '2rem', padding: '20px 24px',
          borderRadius: '14px', background: 'var(--card-bg-secondary, #f8f9fa)',
          border: '1px solid var(--border-color)',
        }}>
          <h4 style={{ margin: '0 0 12px', color: 'var(--text-color)' }}>💙 A few things worth knowing</h4>
          <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--text-secondary-color)', fontSize: '0.88rem', lineHeight: '1.9' }}>
            <li>Your data is always yours — diary entries and reports are never deleted when your plan changes.</li>
            <li>Crisis support (iCall helpline) is always free and never gated — no matter your plan.</li>
            <li>Plus plan is 28 days from purchase date — it does not auto-renew. You choose to renew each month.</li>
            <li>Free plan users keep all previously written diary entries and any generated reports.</li>
            <li>We will never show ads or sell your wellness data. Ever.</li>
          </ul>
        </div>

        {/* Upgrade CTA at bottom for free users */}
        {!isPaid && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              onClick={handleUpgrade}
              disabled={loading}
              style={{
                padding: '14px 40px',
                background: '#c95b3d', color: '#fff',
                border: 'none', borderRadius: '14px',
                fontSize: '1rem', fontWeight: 700,
                cursor: 'pointer', boxShadow: '0 4px 20px rgba(201,91,61,0.3)',
              }}
            >
              Start HealHub Plus for ₹35 →
            </button>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '8px' }}>
              28 days · No auto-renewal · Cancel anytime
            </p>
          </div>
        )}
      </div>
    </div>
  );
}